/**
 * Global TV Offset Bus — Phase 0
 *
 * Single source of truth for the user's TV-vs-app offset (seconds).
 * Replaces the previous fragmented offsets (manual slider, spoiler slider,
 * fake "synced" state) with one deterministic store.
 *
 *   getGlobalOffset()        -> current offset in seconds (signed, clamped)
 *   setGlobalOffset(sec, src)-> persist + notify all subscribers
 *   subscribeOffset(cb)      -> raw subscription, returns unsubscribe
 *   applyOffset(value)       -> delay any reactive value by current offset
 *   useGlobalTvOffset()      -> React hook with offset + setter + state
 *   useDelayedBySync(value)  -> shortcut wrapping applyOffset on bus value
 *
 * Persistence: localStorage only in Phase 0 (lightweight). Future phases
 * may sync to `profile.tv_offset_sec` without changing this API.
 */
import { useEffect, useState, useSyncExternalStore } from "react";

export type SyncState =
  | "idle"
  | "manual_sync"
  | "listening"
  | "analyzing"
  | "mic_denied"
  | "syncing"
  | "synced"
  | "low_confidence"
  | "unsupported";

export type OffsetSource = "manual" | "spoiler" | "mic" | "preset" | "reset" | "system";

const STORAGE_KEY = "vs:tv_offset_sec";
const STATE_KEY = "vs:tv_sync_state";
const LEGACY_KEYS = ["ss2026_manual_offset", "vs:spoiler_offset_sec"];
const EVT = "vs:tv_offset_changed";
// PR-37 — TV Sync rebuild: positive-only offsets. The app can only
// delay itself to match a TV that lags behind the live feed; negative
// offsets are nonsensical (we can't show the future) and previously
// caused the persistent "−60s" stuck state.
const MIN = 0;
const MAX = 60;

function clamp(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(MIN, Math.min(MAX, Math.round(n * 10) / 10));
}

function migrateLegacy(): number | null {
  if (typeof window === "undefined") return null;
  for (const k of LEGACY_KEYS) {
    const raw = window.localStorage.getItem(k);
    if (raw != null) {
      const n = Number(raw);
      if (Number.isFinite(n) && n !== 0) {
        window.localStorage.removeItem(k);
        return clamp(n);
      }
      window.localStorage.removeItem(k);
    }
  }
  return null;
}

function readStored(): number {
  if (typeof window === "undefined") return 0;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw == null) {
    const legacy = migrateLegacy();
    if (legacy != null) {
      window.localStorage.setItem(STORAGE_KEY, String(legacy));
      return legacy;
    }
    return 0;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? clamp(n) : 0;
}

function readStoredState(): SyncState {
  if (typeof window === "undefined") return "idle";
  const v = window.localStorage.getItem(STATE_KEY) as SyncState | null;
  return v ?? "idle";
}

let _offset: number = readStored();
let _state: SyncState = readStoredState();
const listeners = new Set<() => void>();

function notify(): void {
  for (const cb of listeners) {
    try { cb(); } catch { /* noop */ }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVT));
  }
}

/** Current global offset in seconds (signed). */
export function getGlobalOffset(): number {
  return _offset;
}

/** Current sync state machine value. */
export function getSyncState(): SyncState {
  return _state;
}

/** Set the global offset. `src` is for diagnostics/audit. */
export function setGlobalOffset(sec: number, _src: OffsetSource = "manual"): void {
  const next = clamp(sec);
  if (next === _offset) return;
  _offset = next;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  }
  notify();
}

/** Update the visible sync state without mutating the offset. */
export function setSyncState(state: SyncState): void {
  if (state === _state) return;
  _state = state;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STATE_KEY, state);
  }
  notify();
}

/** Raw subscription. Returns unsubscribe. */
export function subscribeOffset(cb: () => void): () => void {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === STATE_KEY) {
      _offset = readStored();
      _state = readStoredState();
      cb();
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(cb);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/** Reset offset and state to zero/idle. */
export function resetGlobalOffset(): void {
  setSyncState("idle");
  setGlobalOffset(0, "reset");
}

/* ---------- React surface ---------- */

const subscribe = (cb: () => void) => subscribeOffset(cb);
const getSnap = (): number => _offset;
const getServerSnap = (): number => 0;
const getStateSnap = (): SyncState => _state;
const getServerStateSnap = (): SyncState => "idle";

export function useGlobalTvOffset() {
  const offsetSec = useSyncExternalStore(subscribe, getSnap, getServerSnap);
  const state = useSyncExternalStore(subscribe, getStateSnap, getServerStateSnap);
  return {
    offsetSec,
    state,
    setOffset: (s: number, src: OffsetSource = "manual") => setGlobalOffset(s, src),
    setState: setSyncState,
    reset: resetGlobalOffset,
  };
}

/**
 * Delay an arbitrary reactive value by `seconds`. Positive offset = the app
 * is ahead of the TV, so emit later. Negative offset = app is behind, pass
 * through immediately (we can't show the future).
 */
export function useDelayedSignal<T>(value: T, seconds: number): T {
  const [delayed, setDelayed] = useState(value);
  useEffect(() => {
    const sec = Math.max(0, seconds);
    if (sec <= 0) {
      setDelayed(value);
      return;
    }
    const t = window.setTimeout(() => setDelayed(value), sec * 1000);
    return () => window.clearTimeout(t);
  }, [value, seconds]);
  return delayed;
}

/**
 * Shortcut — delay a value by the current GlobalTvOffsetBus value.
 * This is the recommended consumer for radar alerts, narrative beats,
 * polls, viral cards, goal toasts, predictive overlays, etc.
 */
export function useDelayedBySync<T>(value: T): T {
  const { offsetSec } = useGlobalTvOffset();
  return useDelayedSignal(value, offsetSec);
}

/** Imperative variant for non-React code (engines, services). */
export function applyOffset<T>(value: T, cb: (v: T) => void): () => void {
  const sec = Math.max(0, _offset);
  if (sec <= 0) {
    cb(value);
    return () => {};
  }
  const t = setTimeout(() => cb(value), sec * 1000);
  return () => clearTimeout(t);
}